/**
 * Create One-Time Job Modal
 * Allows company administrators to create new one-time jobs
 */

import { useState, useRef, type FormEvent } from "react";

// Format a Date as local time for datetime-local input (avoids UTC shift)
function toLocalDateTimeString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
import {
  createOneTimeJob,
  type CreateOneTimeJobInput,
} from "../../../services/oneTimeJob.service";
import { ErrorMessage } from "../../ErrorMessage";
import { BaseModal } from "../../common/BaseModal";
import { ModalButtonGroup } from "../../common/ModalButtonGroup";
import { FormInputField } from "../../form/FormInputField";
import { FormTextareaField } from "../../form/FormTextareaField";
import { LocationAutocompleteField } from "../../form/LocationAutocompleteField";

interface CreateOneTimeJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess: () => void;
}

export function CreateOneTimeJobModal({
  isOpen,
  onClose,
  companyId,
  onSuccess,
}: CreateOneTimeJobModalProps) {
  const [formData, setFormData] = useState<CreateOneTimeJobInput>({
    companyId,
    title: "",
    description: "",
    location: {
      address: "",
      latitude: 0,
      longitude: 0,
    },
    scheduledStart: new Date(),
    scheduledEnd: new Date(),
    hourlyRate: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    const data = formDataRef.current;

    // Validation
    if (!data.title.trim()) {
      setError("One-time job title is required");
      return;
    }

    if (!data.location.address.trim()) {
      setError("Location address is required");
      return;
    }

    if (data.location.latitude === 0 && data.location.longitude === 0) {
      setError("Please select an address from the suggestions to set GPS coordinates");
      return;
    }

    if (data.hourlyRate <= 0) {
      setError("Hourly rate must be greater than 0");
      return;
    }

    if (data.scheduledEnd <= data.scheduledStart) {
      setError("Scheduled end must be after scheduled start");
      return;
    }

    setLoading(true);

    try {
      await createOneTimeJob(data);
      onSuccess();
      onClose();

      // Reset form
      setFormData({
        companyId,
        title: "",
        description: "",
        location: {
          address: "",
          latitude: 0,
          longitude: 0,
        },
        scheduledStart: new Date(),
        scheduledEnd: new Date(),
        hourlyRate: 0,
      });
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to create one-time job",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New One-Time Job"
      size="medium"
      loading={loading}
    >
      <form onSubmit={handleSubmit}>
        <FormInputField
          label="One-Time Job Title"
          value={formData.title}
          onChange={(value) => setFormData({ ...formData, title: value })}
          placeholder="e.g., Night Shift Security Guard"
          required
        />

        <FormTextareaField
          label="Description"
          value={formData.description || ""}
          onChange={(value) => setFormData({ ...formData, description: value })}
          placeholder="One-time job details and requirements..."
          rows={3}
        />

        <LocationAutocompleteField
          label="Location Address"
          onChange={(result) =>
            setFormData((prev) => ({
              ...prev,
              location: result,
            }))
          }
          required
        />

        <FormInputField
          label="Scheduled Start"
          type="datetime-local"
          value={toLocalDateTimeString(formData.scheduledStart)}
          onChange={(value) =>
            setFormData({
              ...formData,
              scheduledStart: new Date(value),
            })
          }
          required
        />

        <FormInputField
          label="Scheduled End"
          type="datetime-local"
          value={toLocalDateTimeString(formData.scheduledEnd)}
          onChange={(value) =>
            setFormData({
              ...formData,
              scheduledEnd: new Date(value),
            })
          }
          required
        />

        <FormInputField
          label="Hourly Rate (€)"
          type="number"
          value={formData.hourlyRate.toString()}
          onChange={(value) =>
            setFormData({
              ...formData,
              hourlyRate: parseFloat(value) || 0,
            })
          }
          min="0"
          step="0.01"
          placeholder="15.00"
          required
        />

        {error && <ErrorMessage message={error} />}

        <ModalButtonGroup
          onCancel={onClose}
          submitLabel="Create One-Time Job"
          isLoading={loading}
          loadingLabel="Creating..."
        />
      </form>
    </BaseModal>
  );
}
